import React, { useState, useEffect, useContext } from "react";
import { AiOutlineDownload } from "react-icons/ai";
import ApiService from "../../../../ApiService";
import SingleOrder from "../KitDetails/SingleOrder";
import JSZip from "jszip";
import Filedownload from "js-file-download";
import { buildChartNotePdf } from "../../../../util/chartNotePdf";
import { SnackbarContext } from "../../../AlertSystem";

export default function PrepProgram({ patientId, patientDetail, patientEmail }) {
  const { showSnackbar } = useContext(SnackbarContext);
  const [prepOrders, setPrepOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    if (!patientEmail) return;
    const fetchOrders = async () => {
      setIsLoading(true);
      try {
        const ordersResponse = await ApiService.getClinicianOrderSummary(patientEmail);
        const orders = Array.isArray(ordersResponse?.data) ? ordersResponse.data : [];
        const filtered = orders.filter((order) =>
          (order?.productName || "").toLowerCase().includes("prep")
        );
        const sorted = filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setPrepOrders(sorted);
      } catch {
        setPrepOrders([]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchOrders();
  }, [patientEmail]);

  const manageEnrollmentUrl = `/super-admin?page=ManagePrepEnrollment&patientId=${encodeURIComponent(patientId || "")}`;

  const handleDownloadAttachment = async () => {
    if (!patientId || !patientDetail) {
      showSnackbar({ message: "Patient information is required to download.", severity: "warning" });
      return;
    }
    setIsDownloading(true);
    try {
      const [notesResponse, bundleResponse] = await Promise.all([
        ApiService.getChartingNotesWithPatientId(patientId),
        ApiService.getPatientPrepDownloadBundle(patientId),
      ]);
      const allNotes = Array.isArray(notesResponse?.data) ? notesResponse.data : [];
      const noteTypeLower = (note) => (note?.noteType || "").toLowerCase();
      const prepNotes = allNotes.filter(
        (note) => noteTypeLower(note).includes("prep") || noteTypeLower(note).includes("beluga")
      );
      const bundle = bundleResponse?.data || {};
      const jotformFiles = Array.isArray(bundle.jotformFiles) ? bundle.jotformFiles : [];
      const rxPdfs = Array.isArray(bundle.rxPdfs) ? bundle.rxPdfs : [];

      const zip = new JSZip();

      for (let i = 0; i < jotformFiles.length; i++) {
        const file = jotformFiles[i];
        const name = file?.filename || `jotform_${i + 1}`;
        const content = file?.content;
        if (content == null) continue;
        if (typeof content === "string" && /^[A-Za-z0-9+/=]+$/.test(content)) {
          zip.file(`jotform/${name}`, content, { base64: true });
        } else {
          zip.file(`jotform/${name}`, content);
        }
      }
      for (let i = 0; i < rxPdfs.length; i++) {
        const file = rxPdfs[i];
        const name = file?.filename || `rx_${i + 1}.pdf`;
        const content = file?.content;
        if (content == null) continue;
        if (typeof content === "string" && /^[A-Za-z0-9+/=]+$/.test(content)) {
          zip.file(`rx/${name}`, content, { base64: true });
        } else {
          zip.file(`rx/${name}`, content);
        }
      }
      for (let i = 0; i < prepNotes.length; i++) {
        const arraybuffer = await buildChartNotePdf(patientDetail, prepNotes[i]);
        zip.file(`clinical_notes/prep_note_${i + 1}.pdf`, arraybuffer);
      }

      const blob = await zip.generateAsync({ type: "blob" });
      const safeName = (patientDetail?.name || "Patient").replace(/[/\\?*:|"]/g, "_").trim() || "Patient";
      Filedownload(blob, `${safeName}_prep_attachments.zip`);
      showSnackbar({ message: "Download started.", severity: "success" });
    } catch (err) {
      console.error("PrEP download failed", err);
      showSnackbar({ message: "Failed to prepare download. Please try again.", severity: "error" });
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="dashboardContent">
      <div
        className="dashboardHeader"
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "12px",
          marginBottom: "16px",
        }}
      >
        <span style={{ fontWeight: "600", fontSize: "1.1rem" }}>PrEP Program</span>
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "10px" }}>
          <button
            type="button"
            onClick={handleDownloadAttachment}
            disabled={isDownloading}
            title="Download chart notes and attachments as a zip file"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              padding: "8px 14px",
              background: isDownloading ? "#e8e8e8" : "transparent",
              color: isDownloading ? "#888" : "#007F61",
              border: `2px solid ${isDownloading ? "#ccc" : "#007F61"}`,
              borderRadius: "8px",
              fontWeight: "600",
              fontSize: "0.875rem",
              cursor: isDownloading ? "not-allowed" : "pointer",
              boxShadow: isDownloading ? "none" : "0 1px 2px rgba(0, 127, 97, 0.1)",
              transition: "background 0.2s ease, color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease",
            }}
            onMouseEnter={(e) => {
              if (isDownloading) return;
              e.currentTarget.style.background = "#e8f5f1";
              e.currentTarget.style.boxShadow = "0 2px 6px rgba(0, 127, 97, 0.15)";
            }}
            onMouseLeave={(e) => {
              if (isDownloading) return;
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.boxShadow = "0 1px 2px rgba(0, 127, 97, 0.1)";
            }}
          >
            <AiOutlineDownload size={18} style={{ flexShrink: 0 }} />
            {isDownloading ? "Preparing…" : "Download attachment"}
          </button>
          <a
            href={manageEnrollmentUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: "inline-flex",
              alignItems: "center",
              padding: "8px 16px",
              background: "#007F61",
              color: "#fff",
              borderRadius: "8px",
              fontWeight: "600",
              fontSize: "0.9rem",
              textDecoration: "none",
              boxShadow: "0 2px 4px rgba(0, 127, 97, 0.25)",
            }}
          >
            Manage PrEP Enrollment
          </a>
        </div>
      </div>
      {isLoading ? (
        <div className="tabsContent px-4">
          <p className="loading"></p>
        </div>
      ) : prepOrders.length > 0 ? (
        <div className="tabsContent px-4">
          {prepOrders.map((order, index) => (
            <SingleOrder key={order?.id || index} order={order} />
          ))}
        </div>
      ) : (
        <div className="tabsContent px-4">
          <h1 className="noRecordFound">No PrEP orders found</h1>
        </div>
      )}
    </div>
  );
}
